import { Injectable } from "@angular/core";
import { BankAccountPermissionsResponse, BankAccountStatus, IBankAccountViewModel } from "src/app/models/bank-account";
import { UserFacade } from "../../user/facade/user.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ClientEntity } from "src/app/models/clients";
import { PermissionResult, PermissionsService } from "src/app/services/http/permission.service";
import { BehaviorSubject, map } from "rxjs";
import { BankAccountService } from "src/app/services/http/bank-account.service";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";

@Injectable()
@UntilDestroy()
export class BankAccountFacade{
    constructor(
        private userFacade: UserFacade,
        private permissionService: PermissionsService,
        private bankAccountService: BankAccountService,
        private generalSelectionFacade: GeneralSelectionFacade
    ){
        this.subscribeToUserEntity();
        this.subscribeToGeneralSelectionSelectedEntities();
        this.reloadBankAccountsOnBankAccountApprovalSuccess();
    }

    private _bankAccountApprovalPermission$ = new BehaviorSubject<BankAccountPermissionsResponse>({
        error: false,
        loading: true,
        data: null
    });
    public bankAccountApprovalPermission$ = this._bankAccountApprovalPermission$.asObservable();

    private userEntityId : number;
    private selectedEntities: ClientEntity[] = [];

    private subscribeToUserEntity() : void {
        this.userFacade.userProfile$.pipe(untilDestroyed(this))
        .subscribe(userProfile => {
            this.userEntityId = !!userProfile ? Number(userProfile.userEntityId) : null;
        });
    }

    private subscribeToGeneralSelectionSelectedEntities(): void {
        this.generalSelectionFacade.getSelectedEntityPortfolioState()
        .pipe(untilDestroyed(this))
        .subscribe((generalState) => {
            this.selectedEntities = generalState.selectedEntities;
        });
    }

    /**
     * Determines if the current user is able to approve or reject the given bank account based on its status and its current approvals.
     */
    public userCanApproveOrRejectGivenBankAccount(bankAccount: IBankAccountViewModel): boolean {
        if(bankAccount === null || bankAccount === undefined)
            return false;

        if(bankAccount.status === null || bankAccount.status === undefined || bankAccount.status !== BankAccountStatus.ApprovalRequired)
            return false;
    
        if(bankAccount.approvalsOutstanding === 0)
            return false;
    
        if(this.userHasAlreadyApprovedBankAccount(bankAccount))
            return false;
    
        return true;
    }

    /**
     * Dispatches a request to retrieve permissions checking whether the logged in user is able to approve bank accounts for each of the given entities.
     * Outputs the response to a seperate observable
     */
    public getCanApproveBankAccountPermissionsPerInvestor(entitites: ClientEntity[]) : void {
        
        this._bankAccountApprovalPermission$.next({
            loading: true,
            error: false,
            data: null
        })

        let bankAccountApprovalPermissionsMap = new Map<number, boolean>();

        this.permissionService.hasPermissionCheckMultiple('BankAccount.Approve', entitites.map(x => x.EntityId))
        .pipe(
            map((permissionResult : PermissionResult[]) => {
            permissionResult.forEach((result) => {
                bankAccountApprovalPermissionsMap.set(result.entityId, result.hasPermission);
            })

            return bankAccountApprovalPermissionsMap;
        }))
        .subscribe((bankAccountApprovalPermissionsMap) => {
            this._bankAccountApprovalPermission$.next({
                loading: false,
                error: false,
                data: bankAccountApprovalPermissionsMap
            })
        })
    }

    /**
     * Dispatches a request to retrieve bank accounts for the selected entities. 
     * Defaults to the bank account statuses of 'active', 'approval required' and 'review required' if none are passed in.
     */
    public getInvestorsBankAccountsForSelectedEntities(statuses: BankAccountStatus[] = null) : void {
        this.getInvestorsBankAccounts(this.selectedEntities.map(x => x.EntityId), statuses)
    }

    /**
     * Dispatches a request to retrieve bank accounts for the given investor entities.
     * Defaults to the bank account statuses of 'active', 'approval required' and 'review required' if none are passed in.
     */
    public getInvestorsBankAccounts(investorEntityIds: number[], statuses: BankAccountStatus[] = null) : void {
        if(investorEntityIds.length === 0)
            return;

        if(statuses === null){
            statuses = [BankAccountStatus.Active, BankAccountStatus.ApprovalRequired, BankAccountStatus.ReviewRequired];
        }

        this.bankAccountService.getInvestorsBankAccounts(investorEntityIds, statuses)
    }
    
    /**
    * Checks the approvals of the given bank account to make sure the current user has not already approved it.
    */
    private userHasAlreadyApprovedBankAccount(bankAccount: IBankAccountViewModel): boolean {
        var idsOfUsersWhoHaveApprovedBankAccount: number[] = bankAccount.approvals.map(x => x.approvedByUserEntityId);
        return idsOfUsersWhoHaveApprovedBankAccount.includes(this.userEntityId);
    }

    /**
     * Listens to the bank account approval response and on success automatically prompts a reload of bank accounts
     */
    private reloadBankAccountsOnBankAccountApprovalSuccess() : void{
        this.bankAccountService.bankAccountActionResponse$
        .subscribe((response) => {
            if(response.loading === false && response.error === false){
                this.getInvestorsBankAccountsForSelectedEntities();
            }
        })
    }
}

